"use client";

import {
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  BrainCircuit,
  Check,
  Clock3,
  Compass,
  Gauge,
  GraduationCap,
  Route,
  Users,
  Workflow,
} from "lucide-react";

import { trackLanding } from "./analytics";
import { CONTACT_URL, DEMO_URL } from "./data";
import {
  Reveal,
  WayakFooter,
  WayakHeader,
  WayakMarketingStyles,
} from "./LandingExperience";
import { ProductPreviewStyles } from "./ProductPreview";
import {
  ServicesProductPreview,
  ServicesProductPreviewStyles,
} from "./ServicesProductPreview";

const PROBLEMS = [
  {
    icon: Clock3,
    number: "01",
    title: "The cycle problem",
    hook: "Important work takes longer than it should.",
    body: "Claims that wait for intake. Quotes that take days. Reporting that consumes the close. We find where context gathering, repeated judgment, and handoffs create elapsed time.",
    outcome: "Compress the cycle without hiding the controls",
  },
  {
    icon: Gauge,
    number: "02",
    title: "The capacity bottleneck",
    hook: "Throughput is capped by the team’s available hours.",
    body: "The opportunity is there, but onboarding, review, or follow-up cannot scale with demand. We redesign the operating loop so people spend their time on the exceptions and decisions that need them.",
    outcome: "Move more work through the same expert team",
  },
];

const OFFERINGS = [
  {
    number: "01",
    icon: Compass,
    title: "AI roadmap and implementation",
    body: "Map the operation, rank the opportunities, and take the best workflow all the way into production. The roadmap is tied to a measurable operating target from day one.",
    items: [
      "Bottleneck and process map",
      "Prioritized workflow portfolio",
      "Production implementation",
      "Before-and-after operating baseline",
    ],
  },
  {
    number: "02",
    icon: Workflow,
    title: "AI process re-engineering",
    body: "Redesign the work around connected context, specialized agents, explicit control points, and reusable playbooks instead of layering a chatbot onto the old process.",
    items: [
      "Future-state operating loop",
      "Context and agent architecture",
      "Systems integration",
      "Human controls and ownership",
    ],
  },
  {
    number: "03",
    icon: GraduationCap,
    title: "Training and enablement",
    body: "Equip executives, operators, and internal builders to use, govern, and extend the system after the first workflow is live. The capability stays with the team.",
    items: [
      "Executive operating sessions",
      "Role-based workshops",
      "Runbooks and guardrails",
      "Internal builder enablement",
    ],
  },
];

function ServicesStyles() {
  return (
    <style>{`
      .sr-shell { width: min(100% - 80px,1360px); margin: 0 auto; }
      .sr-section { padding: 138px 0; }
      .sr-kicker { display: flex; align-items: center; gap: 11px; font-size: 10px; font-weight: 700; letter-spacing: .18em; text-transform: uppercase; }
      .sr-kicker::before { content: ""; width: 26px; height: 1px; background: currentColor; opacity: .5; }
      .sr-title { max-width: 900px; margin: 18px 0 0; font-size: clamp(48px,5.4vw,78px); line-height: .97; letter-spacing: -.05em; }
      .sr-intro { max-width: 690px; margin: 24px 0 0; color: var(--muted); font-size: 18px; line-height: 1.6; }

      .sr-hero-wrap { padding: 12px; }
      .sr-hero { position: relative; min-height: calc(100vh - 92px); overflow: hidden; border-radius: var(--radius-panel); background: var(--ink); color: #fff; }
      .sr-hero::before { content: ""; position: absolute; inset: 0; background-image: linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px); background-size: 48px 48px; mask-image: linear-gradient(to bottom,#000,transparent 90%); }
      .sr-hero::after { content: ""; position: absolute; z-index: 0; width: min(78vw,980px); aspect-ratio: 1; right: -220px; top: -420px; border-radius: 50%; background: radial-gradient(circle,rgba(255,255,255,.11) 0,rgba(255,255,255,.045) 30%,transparent 68%); pointer-events: none; animation: v4AmbientBreath 16s cubic-bezier(.45,0,.55,1) infinite alternate; will-change: transform,opacity; }
      .sr-hero-grid { position: relative; z-index: 2; min-height: calc(100vh - 92px); display: grid; grid-template-columns: .82fr 1.18fr; gap: 72px; align-items: center; padding: 84px 0; }
      .sr-hero-copy .sr-kicker { color: rgba(255,255,255,.68); }
      .sr-hero h1 { max-width: 720px; margin: 24px 0 0; font-size: clamp(58px,6.2vw,96px); line-height: .9; letter-spacing: -.06em; }
      .sr-hero-copy > p { max-width: 620px; margin: 28px 0 0; color: rgba(255,255,255,.66); font-size: 18px; line-height: 1.6; }
      .sr-hero-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 32px; }
      .sr-button { min-height: 48px; display: inline-flex; align-items: center; justify-content: center; gap: 9px; padding: 0 21px; border: 1px solid #fff; border-radius: 10px; background: #fff; color: var(--ink); font-size: 10px; font-weight: 700; letter-spacing: .13em; text-decoration: none; text-transform: uppercase; transition: transform .28s cubic-bezier(.16,1,.3,1),background .22s ease,border-color .22s ease,box-shadow .28s ease; }
      .sr-button.secondary { border-color: rgba(255,255,255,.25); background: rgba(255,255,255,.04); color: #fff; }
      .sr-button:is(:hover,:focus-visible) { transform: translateY(-2px); box-shadow: 0 12px 30px rgba(0,0,0,.18); }
      .sr-button.secondary:is(:hover,:focus-visible) { background: rgba(255,255,255,.1); border-color: rgba(255,255,255,.42); }
      .sr-hero-note { display: flex; align-items: center; gap: 8px; margin-top: 28px; color: rgba(255,255,255,.46); font-size: 10px; line-height: 1.45; }
      .sr-preview-label { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 12px; color: rgba(255,255,255,.52); font-size: 8px; letter-spacing: .12em; text-transform: uppercase; }
      .sr-hero .pv-stage-frame { box-shadow: 0 34px 90px rgba(0,0,0,.38); }

      .sr-proof { padding: 52px 0; border-bottom: 1px solid var(--line); background: #fff; }
      .sr-proof-grid { display: grid; grid-template-columns: repeat(3,1fr); border-top: 1px solid var(--line); }
      .sr-proof-item { min-height: 150px; padding: 28px 28px 10px 0; border-right: 1px solid var(--line); }
      .sr-proof-item:not(:first-child) { padding-left: 28px; }
      .sr-proof-item:last-child { border-right: 0; }
      .sr-proof-item strong { display: block; font-size: clamp(34px,4vw,54px); letter-spacing: -.04em; }
      .sr-proof-item span { display: block; max-width: 250px; margin-top: 10px; color: var(--muted); font-size: 13px; line-height: 1.45; }

      .sr-problems { background: var(--paper); }
      .sr-problem-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 68px; }
      .sr-problem { min-height: 480px; display: flex; flex-direction: column; padding: 32px; border: 1px solid var(--line); border-radius: var(--radius-section); background: var(--soft); }
      .sr-problem-top { display: flex; align-items: center; justify-content: space-between; color: var(--muted); font-size: 9px; letter-spacing: .12em; text-transform: uppercase; }
      .sr-problem-icon { width: 44px; height: 44px; display: grid; place-items: center; margin-top: 74px; border-radius: 12px; background: var(--ink); color: #fff; }
      .sr-problem h3 { margin: 26px 0 0; font-size: clamp(32px,3.6vw,48px); line-height: 1; letter-spacing: -.04em; }
      .sr-problem h4 { margin: 16px 0 0; font-size: 16px; }
      .sr-problem p { max-width: 560px; margin: 12px 0 0; color: var(--muted); font-size: 14px; line-height: 1.6; }
      .sr-problem-outcome { display: flex; align-items: center; gap: 9px; margin-top: auto; padding-top: 32px; font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }

      .sr-method { background: var(--warm); }
      .sr-method-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; margin-top: 68px; }
      .sr-method-step { min-height: 340px; padding: 28px; border: 1px solid var(--line); border-radius: var(--radius-section); background: #fff; }
      .sr-method-step > span { color: var(--muted); font-size: 10px; letter-spacing: .13em; }
      .sr-method-step h3 { margin: 92px 0 0; font-size: 28px; letter-spacing: -.03em; }
      .sr-method-step p { margin: 14px 0 0; color: var(--muted); font-size: 14px; line-height: 1.6; }
      .sr-method-tag { display: inline-flex; align-items: center; gap: 7px; margin-top: 24px; padding: 8px 9px; border: 1px solid var(--line); border-radius: 8px; font-size: 8px; font-weight: 700; letter-spacing: .09em; text-transform: uppercase; }

      .sr-offerings { background: var(--paper); }
      .sr-offering-list { margin-top: 64px; border-top: 1px solid var(--line); }
      .sr-offering { display: grid; grid-template-columns: 70px .8fr 1fr; gap: 42px; padding: 44px 0; border-bottom: 1px solid var(--line); }
      .sr-offering-number { color: var(--muted); font-size: 10px; letter-spacing: .13em; }
      .sr-offering-heading { display: grid; grid-template-columns: 42px 1fr; gap: 14px; }
      .sr-offering-icon { width: 42px; height: 42px; display: grid; place-items: center; border-radius: 11px; background: var(--ink); color: #fff; }
      .sr-offering h3 { margin: 4px 0 0; font-size: 24px; line-height: 1.1; letter-spacing: -.025em; }
      .sr-offering-copy > p { max-width: 600px; margin: 0; color: var(--muted); font-size: 14px; line-height: 1.6; }
      .sr-deliverables { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; margin-top: 20px; }
      .sr-deliverable { display: flex; align-items: center; gap: 8px; font-size: 12px; }
      .sr-deliverable span { width: 18px; height: 18px; display: grid; place-items: center; border-radius: 50%; background: var(--soft); }

      .sr-partner { background: var(--ink); color: #fff; }
      .sr-partner .sr-intro { color: rgba(255,255,255,.58); }
      .sr-partner-grid { display: grid; grid-template-columns: repeat(3,1fr); margin-top: 68px; border-top: 1px solid rgba(255,255,255,.15); border-bottom: 1px solid rgba(255,255,255,.15); }
      .sr-partner-card { min-height: 300px; padding: 26px; border-right: 1px solid rgba(255,255,255,.14); }
      .sr-partner-card:last-child { border-right: 0; }
      .sr-partner-card span { width: 42px; height: 42px; display: grid; place-items: center; border: 1px solid rgba(255,255,255,.2); border-radius: 10px; }
      .sr-partner-card h3 { margin: 86px 0 0; font-size: 15px; letter-spacing: .1em; text-transform: uppercase; }
      .sr-partner-card p { margin: 12px 0 0; color: rgba(255,255,255,.5); font-size: 13px; line-height: 1.55; }

      .sr-close { padding: 16px 16px 0; }
      .sr-close-panel { min-height: 580px; display: grid; place-items: center; padding: 80px 36px; border-radius: var(--radius-panel) var(--radius-panel) 0 0; background: var(--ink); color: #fff; text-align: center; }
      .sr-close-content { max-width: 940px; }
      .sr-close h2 { margin: 0; font-size: clamp(56px,7vw,100px); line-height: .92; letter-spacing: -.055em; }
      .sr-close p { max-width: 620px; margin: 27px auto 0; color: rgba(255,255,255,.62); font-size: 18px; line-height: 1.55; }
      .sr-close-actions { display: flex; justify-content: center; gap: 12px; margin-top: 32px; }

      @media (max-width: 1080px) {
        .sr-hero-grid { grid-template-columns: 1fr; }
        .sr-hero-copy { max-width: 850px; }
        .sr-hero-preview { max-width: 900px; }
        .sr-offering { grid-template-columns: 52px 1fr; }
        .sr-offering-copy { grid-column: 2; }
      }
      @media (max-width: 820px) {
        .sr-shell { width: min(100% - 40px,1360px); }
        .sr-section { padding: 96px 0; }
        .sr-hero-wrap { padding: 8px; }
        .sr-hero { border-radius: 18px; }
        .sr-hero-grid { min-height: auto; gap: 52px; padding: 64px 0 36px; }
        .sr-hero h1 { font-size: clamp(50px,13vw,74px); }
        .sr-proof-grid, .sr-problem-grid, .sr-method-grid, .sr-partner-grid { grid-template-columns: 1fr; }
        .sr-proof-item { min-height: 0; padding: 22px 0; border-right: 0; border-bottom: 1px solid var(--line); }
        .sr-proof-item:not(:first-child) { padding-left: 0; }
        .sr-problem { min-height: 420px; }
        .sr-method-step h3 { margin-top: 54px; }
        .sr-partner-card { min-height: 0; border-right: 0; border-bottom: 1px solid rgba(255,255,255,.14); }
        .sr-partner-card h3 { margin-top: 48px; }
        .sr-close { padding: 8px 8px 0; }
        .sr-close-panel { min-height: 520px; padding: 64px 20px; border-radius: 18px 18px 0 0; }
      }
      @media (max-width: 520px) {
        .sr-shell { width: min(100% - 30px,1360px); }
        .sr-hero-actions, .sr-close-actions { flex-direction: column; }
        .sr-button { width: 100%; }
        .sr-title { font-size: 43px; }
        .sr-offering { grid-template-columns: 1fr; gap: 20px; }
        .sr-offering-heading, .sr-offering-copy { grid-column: 1; }
        .sr-deliverables { grid-template-columns: 1fr; }
      }
      @media (prefers-reduced-motion: reduce) { .sr-hero::after,.sr-button { animation: none !important; transform: none !important; } }
    `}</style>
  );
}

function Hero() {
  return (
    <section id="top" className="sr-hero-wrap">
      <div className="sr-hero">
        <div className="sr-shell sr-hero-grid">
          <Reveal className="sr-hero-copy" immediate>
            <div className="sr-kicker">
              Wayak Services · Platform plus operators
            </div>
            <h1>From operational bottleneck to measurable system.</h1>
            <p>
              We find the work where AI can move cycle time or capacity, build
              the first production workflow with your team, and leave behind a
              system they can run and extend.
            </p>
            <div className="sr-hero-actions">
              <a
                className="sr-button"
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackLanding("landing_demo_click", {
                    location: "services_hero",
                  })
                }
              >
                Book a working session <ArrowUpRight size={14} />
              </a>
              <a className="sr-button secondary" href="#method">
                See how we work <ArrowRight size={14} />
              </a>
            </div>
            <div className="sr-hero-note">
              <Route size={14} /> Bring one workflow that is too slow, too
              manual, or unable to scale.
            </div>
          </Reveal>
          <Reveal className="sr-hero-preview" delay={100} immediate>
            <div className="sr-preview-label">
              <span>Illustrative Wayak engagement</span>
              <span>Baseline → pilot → scale</span>
            </div>
            <ServicesProductPreview />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Proof() {
  const items = [
    ["2", "Shapes of operational pain: cycle time and capacity"],
    ["Weeks", "To a narrow pilot with a measurable operating target"],
    ["In-house", "Your team learns to run and extend the system"],
  ];
  return (
    <section className="sr-proof">
      <div className="sr-shell">
        <Reveal>
          <div className="sr-proof-grid">
            {items.map(([value, label]) => (
              <div className="sr-proof-item" key={label}>
                <strong>{value}</strong>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Problems() {
  return (
    <section className="sr-section sr-problems">
      <div className="sr-shell">
        <Reveal>
          <div className="sr-kicker">Where every engagement starts</div>
          <h2 className="sr-title">
            Operations pain usually has one of two shapes.
          </h2>
          <p className="sr-intro">
            Before models or architecture, we identify which constraint is
            actually limiting the operation. That is what makes the value
            concrete enough to measure.
          </p>
        </Reveal>
        <div className="sr-problem-grid">
          {PROBLEMS.map(({ icon: Icon, ...problem }, index) => (
            <Reveal key={problem.number} delay={index * 80}>
              <article className="sr-problem">
                <div className="sr-problem-top">
                  <span>{problem.number}</span>
                  <span>Operating constraint</span>
                </div>
                <span className="sr-problem-icon">
                  <Icon size={20} />
                </span>
                <h3>{problem.title}</h3>
                <h4>{problem.hook}</h4>
                <p>{problem.body}</p>
                <div className="sr-problem-outcome">
                  <ArrowRight size={15} />
                  {problem.outcome}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Method() {
  const steps = [
    [
      "01",
      "Diagnose",
      "Work inside the operation, map the actual decision path, and measure the constraint before changing it.",
      "Bottleneck + baseline",
    ],
    [
      "02",
      "Prove",
      "Build one production workflow against a narrow target, with the real data, owners, and controls involved.",
      "Live pilot + evidence",
    ],
    [
      "03",
      "Scale",
      "Expand only after the result is visible, train the internal team, and reuse the platform for adjacent work.",
      "Ownership + operating system",
    ],
  ];
  return (
    <section id="method" className="sr-section sr-method">
      <div className="sr-shell">
        <Reveal>
          <div className="sr-kicker">How we work</div>
          <h2 className="sr-title">Diagnose. Prove. Scale.</h2>
          <p className="sr-intro">
            Short, accountable stages with an operating target at each step—not
            a transformation program whose value arrives at the end.
          </p>
        </Reveal>
        <div className="sr-method-grid">
          {steps.map(([number, title, body, tag], index) => (
            <Reveal key={number} delay={index * 70}>
              <article className="sr-method-step">
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{body}</p>
                <div className="sr-method-tag">
                  <BarChart3 size={12} />
                  {tag}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Offerings() {
  return (
    <section className="sr-section sr-offerings">
      <div className="sr-shell">
        <Reveal>
          <div className="sr-kicker">Ways to engage</div>
          <h2 className="sr-title">Strategy that ships. Systems that stay.</h2>
          <p className="sr-intro">
            Start with the stage you need or run the sequence end to end. Each
            engagement is designed around the work, not a generic AI maturity
            model.
          </p>
        </Reveal>
        <Reveal delay={100}>
          <div className="sr-offering-list">
            {OFFERINGS.map(({ icon: Icon, ...offering }) => (
              <article className="sr-offering" key={offering.number}>
                <span className="sr-offering-number">{offering.number}</span>
                <div className="sr-offering-heading">
                  <span className="sr-offering-icon">
                    <Icon size={18} />
                  </span>
                  <h3>{offering.title}</h3>
                </div>
                <div className="sr-offering-copy">
                  <p>{offering.body}</p>
                  <div className="sr-deliverables">
                    {offering.items.map((item) => (
                      <div className="sr-deliverable" key={item}>
                        <span>
                          <Check size={11} />
                        </span>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Partner() {
  const items = [
    [
      BrainCircuit,
      "Platform underneath",
      "The connected context, agents, playbooks, records, and run history stay in one operating layer.",
    ],
    [
      Users,
      "Operators alongside you",
      "We work with the people who own the process so the workflow matches the real exceptions and decisions.",
    ],
    [
      BarChart3,
      "A target you can inspect",
      "Cycle time, touch time, throughput, and exception rate are visible in the run—not buried in a strategy deck.",
    ],
  ] as const;
  return (
    <section className="sr-section sr-partner">
      <div className="sr-shell">
        <Reveal>
          <div className="sr-kicker">Why platform plus services</div>
          <h2 className="sr-title">
            The implementation bridge becomes part of the system.
          </h2>
          <p className="sr-intro">
            You get the speed of a hands-on team and the compounding value of a
            platform your organization owns.
          </p>
        </Reveal>
        <Reveal delay={100}>
          <div className="sr-partner-grid">
            {items.map(([Icon, title, body]) => (
              <article className="sr-partner-card" key={title}>
                <span>
                  <Icon size={18} />
                </span>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Closing() {
  return (
    <section className="sr-close">
      <div className="sr-close-panel">
        <Reveal className="sr-close-content">
          <h2>Let’s find the bottleneck worth fixing first.</h2>
          <p>
            Bring one workflow and thirty minutes. We’ll map where the time or
            capacity disappears and tell you honestly whether Wayak can move it.
          </p>
          <div className="sr-close-actions">
            <a
              className="sr-button"
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Book a working session <ArrowUpRight size={14} />
            </a>
            <a
              className="sr-button secondary"
              href={CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Write to us <ArrowRight size={14} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  return (
    <div className="v4-landing">
      <WayakMarketingStyles />
      <ProductPreviewStyles />
      <ServicesProductPreviewStyles />
      <ServicesStyles />
      <WayakHeader />
      <main>
        <Hero />
        <Proof />
        <Problems />
        <Method />
        <Offerings />
        <Partner />
        <Closing />
      </main>
      <WayakFooter />
    </div>
  );
}
